import {
    flatMap,
    filter,
    find,
    split,
    isEmpty,
    some,
    map,
    includes,
    union,
    pullAll,
    takeWhile,
    chunk,
    every,
    sortBy,
    difference,
} from "../utils/lodash";
import { Skippable } from "../models/Skippable";
import { LogicEvaluator } from "../randomization/Randomization";
import { ElementWithSettings } from "../models/ElementWithSettings";
import {
    getSettingString,
    hasSetting,
    getSettingBoolean,
    getSettingNumber,
} from "../models/utilities/settingUtilities";
import { Indexed } from "../models/Indexed";
import { QuestionnaireModel } from "../models/QuestionnaireModel";
import { QuestionModel } from "../models/QuestionModel";
import { MentionModel } from "../models/MentionModel";
import { isHeaderRow, openEndVariableName } from "../models/utilities/gridUtilities";
import { ChoiceModel } from "../models/ChoiceModel";
import { getVariableWithName, isInputRequired } from "../models/utilities/questionnaireUtilities";
import { AxisModel } from "../models/AxisModel";
import { QuestionType } from "../models/enums/QuestionType";
import { compareChoices, isHeader } from "../models/utilities/choiceUtilities";
import { VariableModel } from "../models/VariableModel";
import {
    getSoftPromptCode,
    isQuestionSoftPrompt,
    shouldSetSoftPromptCode,
} from "../models/utilities/questionUtilities";
import { NavigationResultPageModel } from "../models/NavigationResultPageModel";
import { LogicModel } from "../models/LogicModel";
import { AxisType } from "../models/enums/AxisType";

type EliminationOptions = { includeInvisible?: boolean; includeSoftPrompt?: boolean };

function eliminateSkippable<T extends Skippable>(skippable: T[], evaluator: LogicEvaluator) {
    return skippable.filter((element) => {
        if (element == null) {
            return false;
        }
        if (element.skipLogic != null && evaluator(element.skipLogic)) {
            return false;
        }
        if (element.displayLogic != null && !evaluator(element.displayLogic)) {
            return false;
        }
        return true;
    });
}

function putBackFixedChoices(choices: ChoiceModel[]) {
    const fixedChoices = choices.filter((a) => getSettingBoolean(a, "Fixed"));
    // Exclude fixed choices
    choices = choices.filter((a) => !getSettingBoolean(a, "Fixed"));
    for (const choice of fixedChoices) {
        // Re-add the fixed choices.
        choices.splice(choice.index, 0, choice);
    }
    return choices;
}

const eliminateVarsList = (key: string, ...elements: ElementWithSettings[]) =>
    flatMap(elements, (el) => filter(split(getSettingString(el, key), ","), (v) => !isEmpty(v)));

export function eliminateIndexed<T extends Skippable & Indexed>(
    questionnaire: QuestionnaireModel,
    prefix: string,
    question: QuestionModel,
    mentions: { [variableName: string]: MentionModel[] },
    indexed: T[],
    evaluator: LogicEvaluator,
) {
    const eliminateIfAnsweredList = eliminateVarsList(
        `${prefix}EliminationIfVariablesAnswered`,
        question,
    );

    const eliminateIfNotAnsweredList = eliminateVarsList(
        `${prefix}EliminationIfVariablesNotAnswered`,
        question,
    );

    // Return if no elimination applies.
    const doesEliminationApply =
        !isEmpty(eliminateIfAnsweredList) || !isEmpty(eliminateIfNotAnsweredList);

    if (!doesEliminationApply) {
        return eliminateSkippable(indexed, evaluator);
    }

    // Exclude from the list all headers and non variables elements
    const eliminableElements =
        prefix === "Row" ? filter(indexed, (e) => !isHeaderRow(e as any)) : indexed;
    const eliminableElementsOrdered = sortBy(eliminableElements, (e) => e.index);

    const hasMentions = (c: ChoiceModel, variableName: string) =>
        some(mentions[variableName], (mention) => mention.value === c.value);

    const choicesByVariableName = (variableName: string) => {
        const variable = getVariableWithName(questionnaire, variableName);
        return variable ? variable.choiceList : [];
    };

    const excludedFieldsList = map(
        getSettingString(question, `${prefix}EliminationExcluded`)?.split(","),
        (s) => Number(s) - 1,
    );

    const choiceWithIndex = (variableName: string, index: number) => {
        return find(choicesByVariableName(variableName), (c) => c.index === index);
    };

    const hasMentionWithIndex = (variableName: string, index: number): boolean => {
        const choice = choiceWithIndex(variableName, index);
        return choice && hasMentions(choice, variableName);
    };

    const excludeFromElimination: T[] = filter(eliminableElementsOrdered, (_e, i) =>
        includes(excludedFieldsList, i),
    );

    const eliminateAnswered: T[] = filter(eliminableElementsOrdered, (_e, i) =>
        some(eliminateIfAnsweredList, (variableName) => hasMentionWithIndex(variableName, i)),
    );

    const eliminateNotAnswered: T[] = filter(eliminableElementsOrdered, (_e, i) => {
        return (
            !isEmpty(eliminateIfNotAnsweredList) &&
            every(
                eliminateIfNotAnsweredList,
                (variableName) => !hasMentionWithIndex(variableName, i),
            )
        );
    });

    // Creates an array of unique values to be eliminated.
    const eliminateList = union(eliminateAnswered, eliminateNotAnswered);

    // Removes elements excluded from elimination.
    pullAll(eliminateList, excludeFromElimination);

    // Removes eliminated elements.
    const elements = difference(indexed, eliminateList);

    // Eliminate indexed with skip and display logic.
    return eliminateSkippable(elements, evaluator);
}

export const repeatHeader = (rows: AxisModel[], nthRows: number) => {
    if (!nthRows) {
        return rows;
    }
    const headerRows = takeWhile(rows, isHeaderRow);
    if (headerRows.length === 0) {
        return rows;
    }
    const bodyRows = rows.slice(headerRows.length);
    return chunk(bodyRows, nthRows).reduce((a, b) => [...a, ...headerRows, ...b], []);
};

export const eliminateChoiceList = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    mentions: { [variableName: string]: MentionModel[] },
    choiceList: ChoiceModel[],
    evaluator: LogicEvaluator,
    options: EliminationOptions,
    variable: VariableModel,
    serverPage?: NavigationResultPageModel,
) => {
    let choices: ChoiceModel[] = [...choiceList];
    const { includeInvisible, includeSoftPrompt } = options;
    if (serverPage) {
        choices = choices.filter((c1) =>
            serverPage.questions
                .flatMap((q) => q.variables)
                .find((v) => v.name === variable.name)
                ?.choices.some((c2) => compareChoices(c1, c2, question)),
        );
    } else {
        // Hide invisible choices.
        if (!includeInvisible) {
            const isChoiceVisible = (choice) =>
                !(hasSetting(choice, "Visible") && !getSettingBoolean(choice, "Visible"));
            choices = choices.filter(isChoiceVisible);
        }

        // Eliminate choices with skip and display logic.
        choices = eliminateSkippable(choices, evaluator) as ChoiceModel[];

        choices = putBackFixedChoices(choices);

        const { columns = [], parent } = question;
        const elements: ElementWithSettings[] = [question, ...columns, parent, variable];

        const eliminateIfAnsweredList = eliminateVarsList(
            "EliminateChoicesIfVariablesAnswered",
            ...elements,
        );
        const eliminateIfNotAnsweredList = eliminateVarsList(
            "EliminateChoicesIfVariablesNotAnswered",
            ...elements,
        );

        // Return if no elimination applies.
        const doesEliminationApply =
            !isEmpty(eliminateIfAnsweredList) || !isEmpty(eliminateIfNotAnsweredList);

        if (doesEliminationApply) {
            const excludedFieldsList = eliminateVarsList("EliminationExcludedFields", ...elements);

            const hasMentions = (c: ChoiceModel, key: string) =>
                some(mentions[key], (mention) => mention.value === c.value);

            const excludeFromElimination = choices.filter(
                (choice) => includes(excludedFieldsList, choice.value) || isHeader(choice),
            );

            const eliminateAnswered = choices.filter((c) =>
                some(eliminateIfAnsweredList, (variableName) => hasMentions(c, variableName)),
            );

            const eliminateNotAnswered = !isEmpty(eliminateIfNotAnsweredList)
                ? choices.filter((c) =>
                      every(
                          eliminateIfNotAnsweredList,
                          (variableName) => !hasMentions(c, variableName),
                      ),
                  )
                : [];

            // Creates an array of unique choices to be eliminated.
            const eliminateChoices = union(eliminateAnswered, eliminateNotAnswered);

            // Removes choices excluded from elimination.
            pullAll(eliminateChoices, excludeFromElimination);

            // Removes eliminated variables.
            choices = pullAll(choices, eliminateChoices);
        }
    }

    const questionsWithHeaders = [
        QuestionType.CheckBox,
        QuestionType.DropDownList,
        QuestionType.NumericRanking,
        QuestionType.RadioButton,
        QuestionType.ImageSelector,
    ];
    // Remove choices without value. Only keep them for Radio and Check since it is treated as headers.
    if (!questionsWithHeaders.includes(question.type)) {
        choices = choices.filter((c) => !isEmpty(c.value));
    }

    // Remove soft-prompt choice.
    const isSoftPrompt = isInputRequired(questionnaire) && isQuestionSoftPrompt(question);
    if (isSoftPrompt && !includeSoftPrompt) {
        const softPromptCode = shouldSetSoftPromptCode(question)
            ? getSoftPromptCode(question)
            : null;

        const hasMentions = question.variables.some(
            (v) =>
                !isEmpty(v.choiceList) &&
                mentions[v.name]?.some((mention) => mention.value === softPromptCode),
        );

        if (softPromptCode != null && !hasMentions) {
            choices = choices.filter((choice) => choice.value !== softPromptCode);
        }
    }

    return choices.map((choice) => ({
        ...choice,
        openEndActive: isOpenEndActive(
            question,
            choice,
            serverPage,
            evaluator,
            choice.openEndDisplayLogic,
        ),
    }));
};

export const eliminateVariables = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    mentions: { [variableName: string]: MentionModel[] },
    variables: VariableModel[],
    evaluator: LogicEvaluator,
    options: EliminationOptions,
    serverPage?: NavigationResultPageModel,
) => {
    let visibleVariables: VariableModel[] = [];
    if (serverPage) {
        visibleVariables = variables.filter((v1) =>
            serverPage.questions
                ?.flatMap((q) => q.variables)
                .some((v2) => v1.name.toLowerCase() === v2.name.toLowerCase()),
        );
    } else {
        visibleVariables = eliminateIndexed<VariableModel>(
            questionnaire,
            "Variable",
            question,
            mentions,
            variables,
            evaluator,
        );
    }
    return visibleVariables.map((v) => ({
        ...v,
        choiceList: eliminateChoiceList(
            questionnaire,
            question,
            mentions,
            v.choiceList,
            evaluator,
            options,
            v,
            serverPage,
        ),
    }));
};

function filterServerAxis(
    elements: AxisModel[],
    question: QuestionModel,
    serverPage: NavigationResultPageModel,
    type: AxisType,
) {
    return elements.filter((el) => {
        const serverQuestion = serverPage.questions.find(
            (q) => q.name.toLowerCase() === question.name.toLowerCase(),
        );
        const visibleIndices =
            type === AxisType.Column
                ? serverQuestion?.columnsPositions
                : serverQuestion?.rowsPositions;
        return visibleIndices?.some((i) => el.index === i);
    });
}

export const eliminateRows = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    mentions: { [variableName: string]: MentionModel[] },
    rows: AxisModel[],
    evaluator: LogicEvaluator,
    options: { includeInvisible?: boolean; includeSoftPrompt?: boolean },
    serverPage?: NavigationResultPageModel,
) => {
    let visibleRows: AxisModel[] = [];
    if (serverPage) {
        visibleRows = filterServerAxis(rows, question, serverPage, AxisType.Row);
    } else {
        visibleRows = eliminateIndexed<AxisModel>(
            questionnaire,
            "Row",
            question,
            mentions,
            rows,
            evaluator,
        );
    }
    visibleRows = visibleRows
        .map((axis) => ({
            ...axis,
            variables: axis.variables.map((variable) => ({
                ...variable,
                choiceList: eliminateChoiceList(
                    questionnaire,
                    question,
                    mentions,
                    variable.choiceList,
                    evaluator,
                    options,
                    variable,
                    serverPage,
                ),
            })),
        }))
        .map((row) => ({
            ...row,
            openEndActive: isOpenEndActive(
                question,
                row,
                serverPage,
                evaluator,
                row.openEndDisplayIf,
            ),
        }));
    return repeatHeader(visibleRows, getSettingNumber(question, "RepeatHeader"));
};

function isOpenEndActive(
    question: QuestionModel,
    element: AxisModel | ChoiceModel,
    serverPage: NavigationResultPageModel,
    evaluator: LogicEvaluator,
    displayLogic: LogicModel,
) {
    const variableName = openEndVariableName(element);
    if (serverPage && variableName) {
        return serverPage.questions
            .find((q) => q.name.toLowerCase() === question.name.toLowerCase())
            ?.variables.some((v) => v.name === variableName);
    }
    return !displayLogic || evaluator(displayLogic);
}

export const eliminateColumns = (
    questionnaire: QuestionnaireModel,
    question: QuestionModel,
    mentions: { [variableName: string]: MentionModel[] },
    columns: AxisModel[],
    evaluator: LogicEvaluator,
    options: EliminationOptions,
    serverPage?: NavigationResultPageModel,
) => {
    let visibleColumns: AxisModel[] = [];
    if (serverPage) {
        visibleColumns = filterServerAxis(columns, question, serverPage, AxisType.Column);
    } else {
        visibleColumns = eliminateIndexed<AxisModel>(
            questionnaire,
            "Column",
            question,
            mentions,
            columns,
            evaluator,
        );
    }
    visibleColumns = visibleColumns.map((a) => ({
        ...a,
        variables: a.variables.map((variable) => ({
            ...variable,
            choiceList: eliminateChoiceList(
                questionnaire,
                question,
                mentions,
                variable.choiceList,
                evaluator,
                options,
                variable,
                serverPage,
            ),
        })),
    }));
    return visibleColumns;
};
